<template>
    <div
        class="sidebar-layout"
        :class="{ 'sidebar-layout--hidden': isSidebarHidden }"
    >
        <Toast ref="toastComponent" />

        <!-- Mobile overlay -->
        <div
            v-if="isSidebarOpen"
            class="sidebar-overlay md:hidden"
            aria-hidden="true"
            @click="closeMobileSidebar"
        />

        <!-- Desktop: reveal when fully hidden -->
        <button
            v-if="isSidebarHidden"
            type="button"
            class="sidebar-reveal-btn hidden md:flex"
            aria-label="Show tools sidebar"
            :title="__('Show tools sidebar')"
            @click="showSidebar"
        >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
            <span class="sidebar-reveal-label">{{ __('Tools') }}</span>
        </button>

        <aside
            :class="[
                'sidebar',
                isSidebarOpen ? 'sidebar-open' : 'sidebar-closed',
                isSidebarCollapsed ? 'sidebar-collapsed' : '',
                isSidebarHidden ? 'sidebar-hidden' : '',
            ]"
            :aria-hidden="isSidebarHidden ? 'true' : 'false'"
        >
            <div class="sidebar-content">
                <div class="sidebar-header">
                    <span v-if="!isSidebarCollapsed" class="sidebar-brand">{{ __('Tools') }}</span>
                    <div class="sidebar-header-actions">
                        <!-- Mobile close -->
                        <button
                            type="button"
                            class="sidebar-toggle-btn md:hidden"
                            aria-label="Close tools menu"
                            @click="closeMobileSidebar"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <!-- Desktop: narrow / expand -->
                        <button
                            v-if="!isSidebarHidden"
                            type="button"
                            class="sidebar-toggle-btn hidden md:inline-flex"
                            :aria-label="isSidebarCollapsed ? __('Expand sidebar') : __('Collapse sidebar')"
                            :title="isSidebarCollapsed ? __('Expand sidebar') : __('Collapse sidebar')"
                            @click="toggleSidebarCollapse"
                        >
                            <svg v-if="!isSidebarCollapsed" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                            </svg>
                            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        <!-- Desktop: hide completely -->
                        <button
                            v-if="!isSidebarHidden"
                            type="button"
                            class="sidebar-toggle-btn hidden md:inline-flex"
                            :aria-label="__('Hide sidebar')"
                            :title="__('Hide sidebar')"
                            @click="hideSidebar"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="sidebar-group">
                    <h3 v-if="!isSidebarCollapsed" class="sidebar-group-title">Digital Cards</h3>
                    <nav class="sidebar-nav" aria-label="Digital Cards">
                        <Link
                            v-for="tab in digitalCardsTabs"
                            :key="tab.uri"
                            :href="route('tools.show', tab.uri)"
                            class="sidebar-item"
                            :class="{ 'sidebar-item-active': tab.uri === semanticUri }"
                            :title="isSidebarCollapsed ? tab.name : ''"
                            @click="closeMobileSidebar"
                        >
                            <Icon :name="tab.icon" class="sidebar-icon" />
                            <span v-if="!isSidebarCollapsed" class="sidebar-label">{{ tab.name }}</span>
                        </Link>
                    </nav>
                </div>

                <div class="sidebar-group">
                    <h3 v-if="!isSidebarCollapsed" class="sidebar-group-title">Web Presence</h3>
                    <nav class="sidebar-nav" aria-label="Web Presence">
                        <Link
                            v-for="tab in webPresenceTabs"
                            :key="tab.uri"
                            :href="route('tools.show', tab.uri)"
                            class="sidebar-item"
                            :class="{ 'sidebar-item-active': tab.uri === semanticUri }"
                            :title="isSidebarCollapsed ? tab.name : ''"
                            @click="closeMobileSidebar"
                        >
                            <Icon :name="tab.icon" class="sidebar-icon" />
                            <span v-if="!isSidebarCollapsed" class="sidebar-label">{{ tab.name }}</span>
                        </Link>
                    </nav>
                </div>

                <div class="sidebar-group">
                    <h3 v-if="!isSidebarCollapsed" class="sidebar-group-title">Lead Generation</h3>
                    <nav class="sidebar-nav" aria-label="Lead Generation">
                        <Link
                            v-for="tab in leadGenerationTabs"
                            :key="tab.uri"
                            :href="route('tools.show', tab.uri)"
                            class="sidebar-item"
                            :class="{ 'sidebar-item-active': tab.uri === semanticUri }"
                            :title="isSidebarCollapsed ? tab.name : ''"
                            @click="closeMobileSidebar"
                        >
                            <Icon :name="tab.icon" class="sidebar-icon" />
                            <span v-if="!isSidebarCollapsed" class="sidebar-label">{{ tab.name }}</span>
                        </Link>
                    </nav>
                </div>

                <button
                    v-if="!isSidebarCollapsed && !isSidebarHidden"
                    type="button"
                    class="sidebar-hide-link hidden md:block"
                    @click="hideSidebar"
                >
                    {{ __('Hide sidebar') }}
                </button>
            </div>
        </aside>

        <main
            class="main-content"
            :class="{
                'main-content-collapsed': isSidebarCollapsed && !isSidebarHidden,
                'main-content--wide': wideContent,
                'main-content--sidebar-hidden': isSidebarHidden,
            }"
        >
            <slot />
        </main>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import Icon from './Icon.vue'
import Toast from './Toast.vue'
import { TOOL_GROUPS, URI_TO_SLUG } from '@/utils/toolsRegistry'

const STORAGE_MODE = 'toolsSidebarMode'
const STORAGE_LAST_VISIBLE = 'toolsSidebarLastVisible'

export default {
    components: {
        Link,
        Icon,
        Toast,
    },
    name: 'SidebarNavigation',
    props: {
        uri: String,
        wideContent: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isSidebarOpen: false,
            /** expanded | collapsed | hidden (desktop only) */
            sidebarMode: 'expanded',
            digitalCardsTabs: TOOL_GROUPS.find((g) => g.id === 'digital').tools.map((t) => ({
                name: t.name,
                uri: t.slug,
                icon: t.icon,
            })),
            webPresenceTabs: TOOL_GROUPS.find((g) => g.id === 'web').tools.map((t) => ({
                name: t.name,
                uri: t.slug,
                icon: t.icon,
            })),
            leadGenerationTabs: TOOL_GROUPS.find((g) => g.id === 'leads').tools.map((t) => ({
                name: t.name,
                uri: t.slug,
                icon: t.icon,
            })),
        }
    },
    computed: {
        semanticUri() {
            if (!this.uri) return ''

            if (URI_TO_SLUG[this.uri]) {
                return URI_TO_SLUG[this.uri]
            }

            const allUris = [
                ...this.digitalCardsTabs,
                ...this.webPresenceTabs,
                ...this.leadGenerationTabs,
            ].map((t) => t.uri)

            if (allUris.includes(this.uri)) {
                return this.uri
            }

            return URI_TO_SLUG[this.uri] || this.uri
        },
        isSidebarCollapsed() {
            return this.sidebarMode === 'collapsed'
        },
        isSidebarHidden() {
            return this.sidebarMode === 'hidden'
        },
    },
    methods: {
        toggleMobileSidebar() {
            this.isSidebarOpen = !this.isSidebarOpen
        },
        closeMobileSidebar() {
            this.isSidebarOpen = false
        },
        toggleSidebarCollapse() {
            this.sidebarMode = this.isSidebarCollapsed ? 'expanded' : 'collapsed'
            this.persistSidebarMode()
        },
        hideSidebar() {
            if (window.innerWidth < 768) {
                this.closeMobileSidebar()
                return
            }
            if (this.sidebarMode !== 'hidden') {
                localStorage.setItem(STORAGE_LAST_VISIBLE, this.sidebarMode)
            }
            this.sidebarMode = 'hidden'
            this.persistSidebarMode()
        },
        showSidebar() {
            const last = localStorage.getItem(STORAGE_LAST_VISIBLE)
            this.sidebarMode = last === 'collapsed' ? 'collapsed' : 'expanded'
            this.persistSidebarMode()
        },
        persistSidebarMode() {
            localStorage.setItem(STORAGE_MODE, this.sidebarMode)
        },
        onKeydown(event) {
            if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'b') {
                return
            }
            const tag = event.target?.tagName?.toLowerCase()
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) {
                return
            }
            event.preventDefault()
            if (this.isSidebarHidden) {
                this.showSidebar()
            } else if (this.isSidebarCollapsed) {
                this.sidebarMode = 'expanded'
            } else {
                this.hideSidebar()
            }
            this.persistSidebarMode()
        },
        onResize() {
            if (window.innerWidth >= 768) {
                this.isSidebarOpen = false
            } else if (this.sidebarMode === 'hidden') {
                this.sidebarMode = 'expanded'
            }
        },
    },
    mounted() {
        const saved = localStorage.getItem(STORAGE_MODE)
        if (saved === 'expanded' || saved === 'collapsed' || saved === 'hidden') {
            this.sidebarMode = saved
        } else {
            const legacy = localStorage.getItem('sidebarCollapsed')
            if (legacy === 'true') {
                this.sidebarMode = 'collapsed'
            }
        }

        if (window.innerWidth < 768 && this.sidebarMode === 'hidden') {
            this.sidebarMode = 'expanded'
        }

        window.addEventListener('resize', this.onResize)
        window.addEventListener('keydown', this.onKeydown)

        if (this.$refs.toastComponent) {
            window.$toast = this.$refs.toastComponent
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize)
        window.removeEventListener('keydown', this.onKeydown)
    },
}
</script>

<style scoped>
.sidebar-layout {
    @apply flex min-h-screen w-full min-w-0;
}

.sidebar-mobile-trigger {
    @apply fixed left-3 z-50 flex items-center gap-2 px-3 py-2;
    top: calc(var(--ps-tool-nav-height, 4rem) + 0.75rem);
    @apply bg-white rounded-lg shadow-md border border-gray-200;
    @apply text-gray-700 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100;
    @apply transition-all duration-200;
}

.sidebar-overlay {
    @apply fixed inset-0 bg-black/40 z-40 backdrop-blur-[1px];
}

.sidebar-reveal-btn {
    @apply fixed z-50 flex flex-col items-center justify-center gap-1;
    top: calc(var(--ps-tool-nav-height, 4rem) + 4rem);
    @apply left-0 py-3 px-1.5 rounded-r-lg;
    @apply bg-white border border-l-0 border-gray-200 shadow-md;
    @apply text-green-700 hover:bg-green-50 hover:text-green-800;
    @apply transition-all duration-200;
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
}

.sidebar-reveal-label {
    @apply text-xs font-semibold tracking-wide uppercase;
    letter-spacing: 0.06em;
}

.sidebar {
    @apply fixed md:sticky left-0 z-40 flex flex-col;
    top: var(--ps-tool-nav-height, 4rem);
    @apply bg-white border-r border-gray-200;
    @apply transition-all duration-300 ease-in-out;
    width: 16rem;
    height: calc(100vh - var(--ps-tool-nav-height, 4rem));
    transform: translateX(-100%);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
}

.sidebar-open {
    transform: translateX(0);
}

@media (min-width: 768px) {
    .sidebar {
        transform: translateX(0);
        box-shadow: none;
    }

    .sidebar-closed {
        transform: translateX(0);
    }

    .sidebar-collapsed {
        width: 5rem;
    }

    .sidebar-hidden {
        width: 0;
        min-width: 0;
        opacity: 0;
        pointer-events: none;
        border-right-width: 0;
        overflow: hidden;
    }
}

.sidebar-header {
    @apply flex items-center justify-between gap-2 px-3 py-3 border-b border-gray-200 bg-gray-50/80;
    min-height: 3.25rem;
}

.sidebar-brand {
    @apply text-xs font-semibold text-gray-500 uppercase tracking-wider truncate;
}

.sidebar-header-actions {
    @apply flex items-center gap-0.5 shrink-0;
}

.sidebar-collapsed .sidebar-header {
    @apply justify-center px-2;
}

.sidebar-collapsed .sidebar-brand {
    @apply hidden;
}

.sidebar-toggle-btn {
    @apply p-2 rounded-lg text-gray-500 hover:bg-white hover:text-gray-800;
    @apply active:bg-gray-100 transition-colors duration-150;
    @apply focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:ring-offset-1;
}

.sidebar-content {
    @apply flex flex-col gap-5 p-3 md:p-4 overflow-y-auto flex-1;
    scrollbar-width: thin;
    scrollbar-color: rgba(156, 163, 175, 0.45) transparent;
}

.sidebar-content::-webkit-scrollbar {
    width: 5px;
}

.sidebar-content::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
}

.sidebar-group-title {
    @apply text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1.5;
}

.sidebar-nav {
    @apply flex flex-col gap-0.5;
}

.sidebar-item {
    @apply flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600;
    @apply hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150;
    @apply focus:outline-none focus:ring-2 focus:ring-green-500/30;
    position: relative;
}

.sidebar-collapsed .sidebar-item {
    @apply justify-center px-2 py-2.5;
}

.sidebar-item-active {
    @apply bg-green-50 text-green-800 font-medium hover:bg-green-100 hover:text-green-900;
}

.sidebar-item-active::before {
    content: '';
    @apply absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-green-600 rounded-r;
}

.sidebar-collapsed .sidebar-item-active::before {
    @apply hidden;
}

.sidebar-collapsed .sidebar-item-active {
    @apply bg-green-100 ring-1 ring-green-200/80;
}

.sidebar-icon {
    @apply w-5 h-5 flex-shrink-0 text-current;
}

.sidebar-label {
    @apply text-sm leading-tight truncate;
}

.sidebar-hide-link {
    @apply mt-auto pt-2 text-xs text-gray-400 hover:text-gray-600 text-center w-full;
    @apply border-t border-gray-100 transition-colors;
}

.main-content {
    @apply flex-1 min-w-0 bg-white border border-gray-200 rounded-lg;
    min-height: calc(100vh - var(--ps-tool-nav-height, 4rem) - 1rem);
    @apply transition-all duration-300 ease-in-out;
    margin: 0.75rem;
    margin-left: 0;
    padding: 1rem;
}

@media (min-width: 768px) {
    .main-content {
        margin: 1rem 1rem 1rem 0.75rem;
        padding: 1.25rem 1.5rem;
    }
}

@media (min-width: 1024px) {
    .main-content {
        padding: 1.5rem 2rem;
    }
}

.main-content--wide {
    @apply p-2 sm:p-3 md:p-4 overflow-x-auto;
}

.main-content--sidebar-hidden {
    @apply md:ml-3;
}

.sidebar-layout--hidden .main-content {
    @apply md:ml-2;
}

@media (max-width: 767px) {
    .sidebar {
        display: none;
    }

    .main-content {
        margin: calc(var(--ps-tool-nav-height, 4rem) + 0.5rem) 0.75rem 0.75rem;
        padding: 1rem;
    }
}
</style>
